import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { eventAPI } from '../../../services/eventApi';
import { StaffEventSummary } from '../../../types/event';
import Card from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';
import LoadingSkeleton from '../../../components/ui/LoadingSkeleton';
import ErrorState from '../../../components/ui/ErrorState';
import EmptyState from '../../../components/ui/EmptyState';
import Breadcrumb from '../../../components/layout/Breadcrumb';

export default function EventList() {
  const navigate = useNavigate();
  const [events, setEvents] = useState<StaffEventSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    eventAPI
      .fetchStaffEvents()
      .then((rows) => {
        setEvents([...rows].sort((a, b) => new Date(b.startAt).getTime() - new Date(a.startAt).getTime()));
        setError(null);
      })
      .catch((err) => setError(err instanceof Error ? err : new Error('Failed to load events')))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const remove = async (event: StaffEventSummary) => {
    if (!window.confirm(`Delete “${event.title}”?`)) return;
    setDeletingId(event.id);
    try {
      await eventAPI.deleteEvent(event.id);
      setEvents((current) => current.filter((item) => item.id !== event.id));
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to delete event'));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Breadcrumb items={[{ label: 'Home', to: '/' }, { label: 'Events' }]} />
      <div className="flex items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Manage Events</h1>
          <p className="text-gray-600 mt-2">Create and publish events. The public calendar shows published events only.</p>
        </div>
        <Button onClick={() => navigate('/admin/events/new')}>
          <PlusIcon className="h-5 w-5 mr-2" />
          New event
        </Button>
      </div>

      {error && <ErrorState error={error} title="Could not load events" onRetry={load} />}
      {loading && <LoadingSkeleton lines={6} />}
      {!loading && !error && events.length === 0 && (
        <EmptyState
          title="No events yet"
          description="Create an event to show it on the calendar."
          onAction={() => navigate('/admin/events/new')}
          actionLabel="New event"
        />
      )}
      {!loading && events.length > 0 && (
        <div className="space-y-3">
          {events.map((event) => (
            <Card key={event.id} className="!p-4">
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h2 className="text-base font-semibold text-gray-900 truncate">{event.title}</h2>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${event.status === 'PUBLISHED' ? 'bg-primary/10 text-primary' : 'bg-gray-100 text-gray-600'}`}>
                      {event.status === 'PUBLISHED' ? 'Published' : 'Draft'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">{format(new Date(event.startAt), 'MMMM d, yyyy h:mm a')}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {event.status === 'PUBLISHED' && (
                    <Link to={`/events/${event.slug}`} className="text-sm font-medium text-primary hover:text-primary-dark px-2">
                      View
                    </Link>
                  )}
                  <Button variant="secondary" className="h-10 px-3" onClick={() => navigate(`/admin/events/${event.id}/edit`)}>
                    <PencilIcon className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                  <Button variant="ghost" className="h-10 px-3" disabled={deletingId === event.id} onClick={() => remove(event)}>
                    <TrashIcon className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
